import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { styled } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Unstable_Grid2';

import { LibraryIndicatorT } from 'business/definitions';
import AddToDashboardModal from 'components/ChartEditMenu/AddToDashboardModal';
import { getQueryURL, setChartTemplateAdditionnalQueryParams } from './helpers';

const StyButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.spacing(0.5),
    flexGrow: 1,
    fontSize: 'body1.fontSize',
}));

const StyGridItem = styled(Grid)({
    display: 'flex',
});

type IndicatorsActionsProps = {
    indicator: LibraryIndicatorT;
    isExploreUser: boolean;
    isModel: boolean;
};

const IndicatorActions = ({
    indicator,
    isExploreUser,
    isModel,
}: IndicatorsActionsProps): JSX.Element => {
    const { t } = useTranslation();

    const { dimensions, filters, key, metrics, settings, title, vizType } =
        indicator;

    const navigate = useNavigate();
    const [showAddToDashboardModal, setShowAddToDashboardModal] =
        React.useState(false);

    const handleUseModelClick = () => {
        navigate(
            `/explore?${setChartTemplateAdditionnalQueryParams(
                getQueryURL(filters, dimensions, metrics),
                key,
                isModel,
            )}`,
        );
    };

    const pendoClassNamePrefix = `pendo-indicators-library${isModel ? '-template' : ''}`;
    return (
        <>
            <Grid container spacing={2}>
                <StyGridItem xs>
                    <StyButton
                        className={`${pendoClassNamePrefix}-add-to-dashboard`}
                        onClick={() => setShowAddToDashboardModal(true)}
                        size="small"
                        variant={isExploreUser ? 'outlined' : 'contained'}
                    >
                        {isModel
                            ? t('indicators.addToDashboard')
                            : t('indicators.copyToDashboard')}
                    </StyButton>
                </StyGridItem>
                {isExploreUser && (
                    <StyGridItem xs={6}>
                        <StyButton
                            className={`${pendoClassNamePrefix}-use-model`}
                            onClick={handleUseModelClick}
                            size="small"
                            variant="contained"
                        >
                            {isModel
                                ? t('indicators.useThisModel')
                                : t('indicators.useAsModel')}
                        </StyButton>
                    </StyGridItem>
                )}
            </Grid>
            {showAddToDashboardModal && (
                <AddToDashboardModal
                    show={showAddToDashboardModal}
                    closeModal={() => setShowAddToDashboardModal(false)}
                    query={{
                        metrics,
                        dimensions,
                        filters,
                    }}
                    settings={{
                        selectedViz: vizType,
                        chartTitle: title,
                        chartSettings: settings,
                    }}
                />
            )}
        </>
    );
};

export default IndicatorActions;
